import Vue from 'vue'
import axios from 'axios'

import { methodType, query, VbitJsonApiError } from '@/_type/vbit-api'
import { isArray } from 'lodash'
import store from '@/store'
import { i18n } from '@/plugins/i18n'

import Company from '@/views/Company/models/company'
import Contractor from '@/views/Company/models/contractor'
import ProjectUserContractorAssignmentModel from '@/views/Authorization/models/projectUserContractorAssignment'
import ProjectUserContractorRoleAssignmentModel from '@/views/Authorization/models/projectUserContractorRoleAssignment'
import Role from '@/views/Authorization/models/role'
import User from '@/views/User/models/user'
import Permission from '@/views/Authorization/models/permission'
import Project from '@/views/Project/Project/models/project'
import ProjectUserSettings from '@/views/Project/Project/models/projectUserSettings'
import ProjectGroup from '@/views/Project/Project/models/projectGroup'
import ProjectGroupUserSettings from '@/views/Project/Project/models/projectGroupUserSettings'
import Attachment from '@/views/Attachment/model/attachment'
import NotificationModel from '@/views/Notification/models/notification'
import NotificationResourceModel from '@/views/Notification/models/notificationResource'
import CommentModel from '@/views/Project/Comment/models/comment'
import ThreadModel from '@/views/Project/Comment/models/thread'
import ReactionModel from '@/views/Project/Comment/models/reaction'
import LanguageModel from '@/views/Language/models/language'
import TagModel from '@/views/Language/models/tag'
import StringGroupModel from '@/views/Language/models/stringGroup'
import StringModel from '@/views/Language/models/string'
import StringTokenModel from '@/views/Language/models/stringToken'
import TranslationModel from '@/views/Language/models/translation'
import LanguageStatisticsModel from '@/views/Language/models/languageStatistics'
import TranslationJobModel from '@/views/Language/models/translationJob'
import TextHistoryItemModel from '@/views/Language/models/textHistoryItem'
import StringGroupAttachmentModel from '@/views/Language/models/stringGroupAttachment'
import StringAttachmentModel from '@/views/Language/models/stringAttachment'
import ProjectModuleModel from '@/views/Project/Project/models/projectModule'

class VbitApi {
    headers = {
        'Content-Type': 'application/vnd.api+json',
    }

    private initHeaders() {
        this.headers = {
            'Content-Type': 'application/vnd.api+json',
        }
    }

    private setReturnExistingEmailMobileErrors(ReturnExistingEmailMobileErrors?: boolean) {
        if (ReturnExistingEmailMobileErrors) {
            this.headers['ReturnExistingEmailMobileErrors'] = true
        } else {
            delete this.headers['ReturnExistingEmailMobileErrors']
        }
    }

    private setUseMfa(UseMfa?: boolean) {
        if (UseMfa) {
            this.headers['UseMfa'] = true
        } else {
            delete this.headers['UseMfa']
        }
    }

    private setToken(token?: boolean) {
        if (token) {
            this.headers['Authorization'] = `TranslationJob ${token}`
        } else {
            delete this.headers['Authorization']
        }
    }

    private setContentType(contentType?: string) {
        if (contentType) {
            this.headers['Content-Type'] = contentType
        } else {
            this.headers['Content-Type'] = 'application/vnd.api+json'
        }
    }

    private setContentDisposition(contentDisposition?: string) {
        if (contentDisposition) {
            this.headers['Content-Disposition'] = contentDisposition
        } else {
            delete this.headers['Content-Disposition']
        }
    }

    public setCustomHeaders(data: any) {
        this.headers = data
    }

    // If Effective string must always call production api
    public url(module: string, endpoint: string, query?) {
        let url =
            module != 'language'
                ? `https://${module}.${process.env.VUE_APP_VBIT_API}/${endpoint}${
                      query ? this.parseQuery(query) : ''
                  }`
                : `https://${module}.api.vbit.com/${endpoint}${query ? this.parseQuery(query) : ''}`
        return url
    }

    private parseQuery(query) {
        // will be removed it the future
        this.setUseMfa(query.useMfa)
        this.setToken(query.token)
        this.setReturnExistingEmailMobileErrors(query.ReturnExistingEmailMobileErrors)
        let url = ''

        let include =
            query.include && query.include.length > 0 ? 'include=' + query.include.join(',') : null

        let skipLinks = query.skipLinks ? 'skipLinks' : null

        let filters = query.filters ? '' : null

        if (query.filters)
            Object.entries(query.filters).map(([k, v], i) => {
                filters += i == 0 ? '' : '&'
                filters += `${k}=${
                    isArray(v) ? encodeURIComponent(v.join(',')) : encodeURIComponent('' + v)
                }`
            })

        let arr: Array<string> = []

        if (include) arr.push(include)
        if (skipLinks) arr.push(skipLinks)
        if (filters) arr.push(filters)
        if (typeof query.dryRun !== 'undefined') arr.push(`dryRun=${query.dryRun}`)

        url = `${arr.length > 0 ? '?' : ''}${arr.join('&')}`

        return url
    }

    // GET
    public async get(module: string, endpoint: string, query?) {
        this.initHeaders()
        let url = this.url(module, endpoint, query)
        return await this.request('get', url, query?.body, query)
    }

    // PATCH
    public async patch(module, endpoint, query?) {
        this.initHeaders()
        let url = this.url(module, endpoint, query)
        return await this.request('patch', url, query?.body, query)
    }

    // POST
    public async post(module, endpoint, query) {
        this.initHeaders()
        let url = this.url(module, endpoint, query)
        return await this.request('post', url, query?.body, query)
    }

    // POST FILE
    public async postFile(module, endpoint, query, header?) {
        this.initHeaders()
        let url = this.url(module, endpoint, query)
        return await this.request('post', url, query?.body, query)
    }

    public async postAttachment(query, file?) {
        this.initHeaders()
        let url = this.url('attachment', 'files')
        return await this.request('post', url, file, query)
    }

    // DELETE
    public async delete(module, endpoint, query?) {
        this.initHeaders()
        let url = this.url(module, endpoint, query)
        return await this.request('delete', url, query?.body, query)
    }

    // HANDLE ERROR
    protected errorHandler(
        errors: Array<VbitJsonApiError>,
        headers: Record<string, string>,
        url: string,
        operation: string
    ) {
        let error = errors[0]

        if (!error.code && errors.length > 1) {
            let index = errors.findIndex((err) => err.code)
            if (index > 0) error = errors[index]
        }

        switch (error.code) {
            case 'invalidOrgNoForNationality':
            case 'invalidCredentials':
            case 'userNotFound':
            case 'userDisabled':
            case 'incorrectPassword':
            case 'emailNotVerified':
            case 'mobileNotVerified':
                return error
            case 'usernameRateLimited':
                return error
            case 'proofRequired':
                error['claim'] = headers['mfa-proof-claim']
                return error
            case 'invalidSecret':
                return error
            case 'challengeRateLimited':
                error['retryAfter'] = headers['retry-after']
                return error
            case 'emailAlreadyInAccount':
                return error
            case 'emailExistsOnOtherUser':
                return error
            case 'mobileExistsOnOtherUser':
                return error
            case 'unauthenticated':
                // sign in dialog
                store.commit('authenticated', false)
                console.log(
                    'Although the HTTP standard specifies "unauthorized", semantically this response means "unauthenticated". That is, the client must authenticate itself to get the requested response.'
                )
                return null
            case 'forbidden':
                console.log(
                    'The client does not have access rights to the content; that is, it is unauthorized, so the server is refusing to give the requested resource. Unlike 401 Unauthorized, the clients identity is known to the server.'
                )
                store.commit('setErrorSnackBar', {
                    data: errors,
                    text: i18n.t('vbit_error.default_header'),
                    url: `${operation}: ${url}`,
                })
                return error
            case undefined:
                // commit snack bar with general error and report to sentry
                store.commit('setErrorSnackBar', {
                    data: errors,
                    text: i18n.t('vbit_error.default_header'),
                    url: `${operation}: ${url}`,
                })
                return error
            default:
                // error that should be handled manually
                store.commit('setErrorSnackBar', {
                    data: errors,
                    text: i18n.t('vbit_error.default_header'),
                    url: `${operation}: ${url}`,
                })

                return error
        }
    }

    private controller = new AbortController()

    public cancelRequest() {
        this.controller.abort()
    }

    private request = async (method: methodType, url: string, body: any, query?: query) => {
        if (query?.updatedAt) {
            // clear If-Unmodified-Since header
            delete query.updatedAt
        }

        if (query?.contentType) {
            this.setContentType(query.contentType)
            delete query.contentType
        }

        if (query?.contentDisposition) {
            this.setContentDisposition(query.contentDisposition)
            delete query.contentDisposition
        }

        if (query?.useDetailedSignInErrors) {
            this.headers['UseDetailedSignInErrors'] = true
            delete query.useDetailedSignInErrors
        }

        let responseType = query?.responseType ? query.responseType : 'json'

        let promise: any = await new Promise((resolve, reject) => {
            let time = new Date().getTime()

            axios(url, {
                method: method,
                headers: this.headers,
                withCredentials: true,
                data: body,
                responseType: responseType,
                ...(query && { signal: query.abortSignal }),
            })
                .then(function (response: any) {
                    response.ms = new Date().getTime() - time
                    if (responseType == 'blob') {
                        resolve(response?.data ? response.data : response)
                    } else {
                        resolve(response)
                    }
                })
                .catch((error) => {
                    this.initHeaders()
                    if (error.response) {
                        reject(
                            this.errorHandler(
                                error.response.data.errors,
                                error.response.headers,
                                url,
                                method.toUpperCase()
                            )
                        )
                    } else {
                        reject(error)
                    }
                })
        })

        this.initHeaders()
        if (promise) {
            let included = promise.data.included ? this.assignClass(promise.data.included) : []

            let response = {
                status: promise.status,
                data: promise.data.data ? promise.data.data : promise.data,
                included: included,
                headers: promise.headers,
            }
            return response
        }

        return null
    }

    assignClass(included: Array<any>) {
        return included.map((item) => {
            switch (item.type) {
                case 'attachment':
                    return new Attachment(item)
                case 'company':
                    return new Company(item)
                case 'contractor':
                    return new Contractor(item)
                case 'role':
                    return new Role(item)
                case 'user':
                    return new User(item)
                case 'permission':
                    return new Permission(item)
                case 'project':
                    return new Project(item)
                case 'projectUserSettings':
                    return new ProjectUserSettings(item)
                case 'projectGroup':
                    return new ProjectGroup(item)
                case 'projectGroupUserSettings':
                    return new ProjectGroupUserSettings(item)
                case 'projectUserContractorAssignment':
                    return new ProjectUserContractorAssignmentModel(item)
                case 'projectUserContractorRoleAssignment':
                    return new ProjectUserContractorRoleAssignmentModel(item)
                case 'projectModule':
                    return new ProjectModuleModel(item)
                case 'notification':
                    return new NotificationModel(item)
                case 'notificationResource':
                    return new NotificationResourceModel(item)
                case 'comment':
                    return new CommentModel(item)
                case 'thread':
                    return new ThreadModel(item)
                case 'reaction':
                    return new ReactionModel(item)
                case 'language':
                    return new LanguageModel(item)
                case 'tag':
                    return new TagModel(item)
                case 'stringGroup':
                    return new StringGroupModel(item)
                case 'stringGroupAttachment':
                    return new StringGroupAttachmentModel(item)
                case 'string':
                    return new StringModel(item)
                case 'stringToken':
                    return new StringTokenModel(item)
                case 'stringAttachment':
                    return new StringAttachmentModel(item)
                case 'translation':
                    return new TranslationModel(item)
                case 'translationJob':
                    return new TranslationJobModel(item)
                case 'textHistoryItem':
                    return new TextHistoryItemModel(item)
                case 'languageStatistics':
                    return new LanguageStatisticsModel(item)
                default:
                    return item
            }
        })
    }

    public download = async (url, filename) => {
        let promise: any = await new Promise((resolve, reject) => {
            axios(url, {
                method: 'get',
                headers: this.headers,
                withCredentials: true,
                responseType: 'blob',
            })
                .then(function (response: any) {
                    const url = window.URL.createObjectURL(new Blob([response.data]))
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', filename)
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    resolve(response)
                })
                .catch((error) => {
                    this.initHeaders()
                    if (error.response) {
                        reject(
                            this.errorHandler(
                                error.response.data.errors,
                                error.response.headers,
                                url,
                                'GET'
                            )
                        )
                    } else {
                        reject(error)
                    }
                })
        })

        return promise as Promise<any>
    }
}

// export default VBITApi
Vue.use((vm: any) => {
    vm.prototype.$vbit = new VbitApi()
})

declare module 'vue/types/vue' {
    interface Vue {
        $vbit: VbitApi
    }
}
